import { zodResolver } from "@hookform/resolvers/zod";
import { changePasswordRequestSchema, type SessionInfo } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy, Laptop, Smartphone } from "lucide-react";
import QRCode from "qrcode";
import { type ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { api } from "../../../lib/api.js";
import { applyFieldErrors, errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";
import { useDocumentTitle } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";
import { useMe, usePermissions } from "../../../lib/session.js";
import { useStepUp } from "../../../lib/step-up.js";

export const Route = createFileRoute("/_app/ayarlar/guvenlik")({ component: SecurityPage });

function SecurityPage() {
  const { t } = useTranslation();
  const me = useMe().data;
  const canManageTenant = usePermissions().has("iam.security.manage");
  useDocumentTitle(t("menu.security"));
  if (!me) return <SkeletonRows rows={6} />;

  return (
    <>
      <PageHeader
        title={t("menu.security")}
        description={t("security.description")}
        breadcrumb={[{ label: t("menu.groups.settings") }, { label: t("menu.security") }]}
      />
      <div className="grid max-w-3xl gap-4">
        <MfaCard enabled={me.user.mfaEnabled} required={me.activeTenant?.mfaRequired ?? false} />
        <PasswordCard />
        <SessionsCard />
        {canManageTenant && me.activeTenant ? (
          <TenantSecurityCard mfaRequired={me.activeTenant.mfaRequired} userHasMfa={me.user.mfaEnabled} />
        ) : null}
      </div>
    </>
  );
}

function SectionCard({
  title,
  description,
  badge,
  children,
}: {
  title: string;
  description: string;
  badge?: ReactElement;
  children: React.ReactNode;
}): ReactElement {
  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-1">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {badge}
      </div>
      {children}
    </Card>
  );
}

// ── İki adımlı doğrulama ──────────────────────────────────────────────────────
function RecoveryCodes({ codes }: { codes: string[] }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  return (
    <div className="grid gap-3">
      <Alert tone="warning" title={t("security.mfa.recoveryTitle")}>
        {t("security.mfa.recoveryDescription")}
      </Alert>
      <ul
        className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-4 font-mono text-sm"
        aria-label={t("security.mfa.recoveryTitle")}
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <Button
        variant="outline"
        className="justify-self-start"
        onClick={() =>
          void navigator.clipboard
            .writeText(codes.join("\n"))
            .then(() => toast({ tone: "success", title: t("common.copied") }))
        }
      >
        <Copy aria-hidden="true" />
        {t("common.copy")}
      </Button>
    </div>
  );
}

function MfaCard({ enabled, required }: { enabled: boolean; required: boolean }): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<{ secret: string; qrDataUrl: string } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [dialog, setDialog] = useState<"disable" | "regenerate" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCode("");
    setPassword("");
    setError(null);
  };
  const onError = (caught: unknown) => setError(errorMessage(caught));

  const start = useMutation({
    mutationFn: async () => {
      const { data } = await api.POST("/auth/mfa/setup");
      // QR tarayıcıda üretilir: sır üçüncü taraf bir QR servisine gönderilmez.
      return {
        secret: data!.secret,
        qrDataUrl: await QRCode.toDataURL(data!.otpauthUrl, { margin: 1, width: 200 }),
      };
    },
    onSuccess: (data) => {
      reset();
      setSetup(data);
    },
    onError,
  });
  const enable = useMutation({
    mutationFn: async () => (await api.POST("/auth/mfa/enable", { body: { code } })).data!,
    onSuccess: async (data) => {
      setSetup(null);
      reset();
      setRecoveryCodes(data.recoveryCodes);
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
    onError,
  });
  const disable = useMutation({
    mutationFn: async () => api.POST("/auth/mfa/disable", { body: { password, code } }),
    onSuccess: async () => {
      setDialog(null);
      reset();
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
    onError,
  });
  const regenerate = useMutation({
    mutationFn: async () => (await api.POST("/auth/mfa/recovery-codes", { body: { code } })).data!,
    onSuccess: (data) => {
      setDialog(null);
      reset();
      setRecoveryCodes(data.recoveryCodes);
    },
    onError,
  });

  const codeField = (
    <FormField label={t("auth.mfa.code")} description={t("security.mfa.codeHint")} required>
      {(control) => (
        <Input
          {...control}
          value={code}
          onChange={(event) => setCode(event.target.value.trim())}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={11}
          className="max-w-48 font-mono tracking-widest"
        />
      )}
    </FormField>
  );

  return (
    <SectionCard
      title={t("security.mfa.title")}
      description={t("security.mfa.description")}
      badge={
        <StatusBadge tone={enabled ? "success" : required ? "danger" : "neutral"}>
          {enabled ? t("common.on") : required ? t("security.mfa.requiredBadge") : t("common.off")}
        </StatusBadge>
      }
    >
      {error && dialog === null ? (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      {recoveryCodes ? (
        <div className="grid gap-3">
          <RecoveryCodes codes={recoveryCodes} />
          <Button className="justify-self-start" onClick={() => setRecoveryCodes(null)}>
            {t("security.mfa.recoverySaved")}
          </Button>
        </div>
      ) : setup ? (
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            enable.mutate();
          }}
        >
          <ol className="grid list-decimal gap-1 pl-5 text-sm text-muted-foreground">
            <li>{t("security.mfa.step1")}</li>
            <li>{t("security.mfa.step2")}</li>
            <li>{t("security.mfa.step3")}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-5">
            <img
              src={setup.qrDataUrl}
              alt={t("security.mfa.qrAlt")}
              width={200}
              height={200}
              className="rounded-md border bg-white p-2"
            />
            <div className="grid gap-1 text-sm">
              <span className="text-muted-foreground">{t("security.mfa.manualKey")}</span>
              <code className="break-all rounded bg-muted px-2 py-1 font-mono">{setup.secret}</code>
            </div>
          </div>
          {codeField}
          <div className="flex gap-2">
            <Button type="submit" loading={enable.isPending} disabled={code.length !== 6}>
              {t("security.mfa.enable")}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSetup(null);
                reset();
              }}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : enabled ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              reset();
              setDialog("regenerate");
            }}
          >
            {t("security.mfa.regenerate")}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              setDialog("disable");
            }}
          >
            {t("security.mfa.disable")}
          </Button>
        </div>
      ) : (
        <Button onClick={() => start.mutate()} loading={start.isPending}>
          {t("security.mfa.setup")}
        </Button>
      )}

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        title={dialog === "disable" ? t("security.mfa.disable") : t("security.mfa.regenerate")}
        description={
          dialog === "disable"
            ? required
              ? t("security.mfa.disableRequiredWarning")
              : t("security.mfa.disableDescription")
            : t("security.mfa.regenerateDescription")
        }
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (dialog === "disable") disable.mutate();
            else regenerate.mutate();
          }}
        >
          {error ? <Alert tone="danger">{error}</Alert> : null}
          {dialog === "disable" ? (
            <FormField label={t("auth.password")} required>
              {(control) => (
                <Input
                  {...control}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </FormField>
          ) : null}
          {codeField}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialog(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant={dialog === "disable" ? "destructive" : "primary"}
              loading={disable.isPending || regenerate.isPending}
              disabled={code.length < 6 || (dialog === "disable" && password.length === 0)}
            >
              {dialog === "disable" ? t("security.mfa.disable") : t("security.mfa.regenerate")}
            </Button>
          </div>
        </form>
      </Dialog>
    </SectionCard>
  );
}

// ── Parola ────────────────────────────────────────────────────────────────────
type PasswordValues = z.input<typeof changePasswordRequestSchema>;

function PasswordCard(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<PasswordValues>({
    resolver: zodResolver(changePasswordRequestSchema),
    defaultValues: { currentPassword: "", newPassword: "" },
  });
  const { errors, isSubmitting } = form.formState;

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.POST("/auth/password/change", { body: values });
      form.reset();
      toast({
        tone: "success",
        title: t("security.password.changed"),
        description: t("security.password.changedHint"),
      });
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, ["currentPassword", "newPassword"]).length > 0 ||
        Object.keys(form.formState.errors).length === 0
      )
        setFormError(errorMessage(error));
    }
  });

  return (
    <SectionCard title={t("security.password.title")} description={t("security.password.description")}>
      <form onSubmit={(event) => void submit(event)} className="grid max-w-sm gap-4" noValidate>
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        <FormField label={t("security.password.current")} error={errors.currentPassword?.message} required>
          {(control) => (
            <Input
              {...control}
              {...form.register("currentPassword")}
              type="password"
              autoComplete="current-password"
            />
          )}
        </FormField>
        <FormField
          label={t("security.password.new")}
          description={t("auth.passwordHint")}
          error={errors.newPassword?.message}
          required
        >
          {(control) => (
            <Input
              {...control}
              {...form.register("newPassword")}
              type="password"
              autoComplete="new-password"
            />
          )}
        </FormField>
        <Button type="submit" className="justify-self-start" loading={isSubmitting}>
          {t("security.password.change")}
        </Button>
      </form>
    </SectionCard>
  );
}

// ── Oturumlar ─────────────────────────────────────────────────────────────────
function SessionsCard(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmOthers, setConfirmOthers] = useState(false);
  const sessions = useQuery({
    queryKey: queryKeys.sessions,
    queryFn: async () => (await api.GET("/auth/sessions")).data!.items,
  });
  const onDone = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    toast({ tone: "success", title: t("security.sessions.revoked") });
  };
  const revoke = useMutation({
    mutationFn: async (id: string) => api.DELETE("/auth/sessions/{id}", { params: { path: { id } } }),
    onSuccess: onDone,
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  const revokeOthers = useMutation({
    mutationFn: async () => api.POST("/auth/sessions/revoke-others"),
    onSuccess: onDone,
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  const others = (sessions.data ?? []).filter((session) => !session.current).length;

  return (
    <SectionCard title={t("security.sessions.title")} description={t("security.sessions.description")}>
      {sessions.error ? <Alert tone="danger">{errorMessage(sessions.error)}</Alert> : null}
      {sessions.isPending ? (
        <SkeletonRows rows={2} />
      ) : (
        <ul className="grid gap-2">
          {(sessions.data ?? []).map((session: SessionInfo) => {
            const Icon = session.clientKind === "mobile" ? Smartphone : Laptop;
            return (
              <li key={session.id} className="flex flex-wrap items-center gap-3 rounded-md border px-4 py-3">
                <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
                <div className="grid min-w-0 flex-1 gap-0.5 text-sm">
                  <span className="flex flex-wrap items-center gap-2 font-medium">
                    <span className="truncate">
                      {session.deviceLabel ?? session.userAgent ?? t("security.sessions.unknownDevice")}
                    </span>
                    {session.current ? (
                      <StatusBadge tone="success">{t("security.sessions.current")}</StatusBadge>
                    ) : null}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {[
                      session.ip,
                      t("security.sessions.lastUsed", { date: formatDateTime(session.lastUsedAt) }),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                {session.current ? null : (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(session.id)}
                  >
                    {t("security.sessions.revoke")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {others > 0 ? (
        <Button variant="outline" className="mt-4" onClick={() => setConfirmOthers(true)}>
          {t("security.sessions.revokeOthers")}
        </Button>
      ) : null}
      <ConfirmDialog
        open={confirmOthers}
        onOpenChange={setConfirmOthers}
        title={t("security.sessions.revokeOthers")}
        description={t("security.sessions.revokeOthersConfirm", { count: others })}
        tone="danger"
        confirmLabel={t("security.sessions.revokeOthers")}
        onConfirm={() => revokeOthers.mutateAsync().then(() => undefined)}
      />
    </SectionCard>
  );
}

// ── Kiracı güvenlik ayarı ─────────────────────────────────────────────────────
function TenantSecurityCard({
  mfaRequired,
  userHasMfa,
}: {
  mfaRequired: boolean;
  userHasMfa: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const withStepUp = useStepUp();
  const [checked, setChecked] = useState(mfaRequired);
  useEffect(() => setChecked(mfaRequired), [mfaRequired]);

  const save = useMutation({
    mutationFn: async (next: boolean) =>
      withStepUp(() => api.PUT("/tenant/security", { body: { mfaRequired: next } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: (error) => {
      setChecked(mfaRequired);
      toast({ tone: "danger", title: errorMessage(error) });
    },
  });

  return (
    <SectionCard title={t("security.tenant.title")} description={t("security.tenant.description")}>
      {!userHasMfa && !mfaRequired ? (
        <Alert tone="info" className="mb-4">
          {t("security.tenant.enableOwnFirst")}
        </Alert>
      ) : null}
      <label className="flex items-start gap-3 text-sm">
        <Checkbox
          className="mt-0.5"
          checked={checked}
          disabled={save.isPending || (!userHasMfa && !mfaRequired)}
          onChange={(event) => {
            setChecked(event.target.checked);
            save.mutate(event.target.checked);
          }}
        />
        <span className="grid gap-0.5">
          <span className="font-medium">{t("security.tenant.mfaRequired")}</span>
          <span className="text-muted-foreground">{t("security.tenant.mfaRequiredHint")}</span>
        </span>
      </label>
    </SectionCard>
  );
}
